import React, { useEffect, useState } from 'react';
import { X, Flame } from 'lucide-react';

const MILESTONES = {
  3: { title: '3-day streak', message: 'Your ritual is becoming a rhythm.' },
  7: { title: 'A full week', message: 'Seven days of showing up. Beautifully done.' },
  14: { title: 'Two weeks strong', message: 'You\u2019re building something real.' },
  30: { title: 'A month of magic', message: 'Thirty days of caring for yourself.' },
  60: { title: 'Sixty days deep', message: 'This is no longer a habit \u2014 it\u2019s a way of being.' },
  100: { title: '100 days in a row', message: 'You are a walking love letter to yourself.' },
};

const getMilestone = (streak) => MILESTONES[streak];

const Confetti = () => {
  // Simple CSS confetti \u2014 lightweight, no external lib.
  const colors = ['#7a8b6b', '#c9a24a', '#d4a89b', '#a7c4a0', '#e0b4b4', '#c9b8d6'];
  const pieces = Array.from({ length: 40 });
  return (
    <div className="streak-confetti pointer-events-none absolute inset-0 overflow-hidden">
      {pieces.map((_, i) => {
        const left = Math.random() * 100;
        const delay = Math.random() * 0.6;
        const duration = 2.4 + Math.random() * 1.6;
        const color = colors[i % colors.length];
        const rotate = Math.random() * 360;
        const size = 6 + Math.random() * 6;
        return (
          <span
            key={i}
            className="absolute top-[-20px] rounded-sm"
            style={{
              left: `${left}%`,
              width: `${size}px`,
              height: `${size * 0.4}px`,
              backgroundColor: color,
              transform: `rotate(${rotate}deg)`,
              animation: `hwl-fall ${duration}s ${delay}s ease-in forwards`,
            }}
          />
        );
      })}
    </div>
  );
};

const StreakCelebration = () => {
  const [event, setEvent] = useState(null); // { streak, recipeTitle }

  useEffect(() => {
    const handler = (e) => {
      const { streak, recipeTitle } = e.detail || {};
      if (getMilestone(streak)) {
        setEvent({ streak, recipeTitle });
        // Auto-dismiss after 6s
        setTimeout(() => setEvent(null), 6000);
      }
    };
    window.addEventListener('hwl:streak-milestone', handler);
    return () => window.removeEventListener('hwl:streak-milestone', handler);
  }, []);

  if (!event) return null;
  const m = getMilestone(event.streak);
  if (!m) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-6 bg-black/40 backdrop-blur-sm streak-fade" onClick={() => setEvent(null)}>
      <Confetti />
      <div
        className="relative max-w-md w-full bg-[#faf7f1] shadow-2xl p-10 text-center streak-pop"
        onClick={e => e.stopPropagation()}
      >
        <button
          onClick={() => setEvent(null)}
          aria-label="Close"
          className="absolute top-3 right-3 text-[#6f6a5f] hover:text-[#3a4a3b]"
        >
          <X className="w-5 h-5" />
        </button>
        <div className="relative w-24 h-24 mx-auto mb-4 flex items-center justify-center">
          <div className="absolute inset-0 rounded-full bg-[#7a3b2e]/10 streak-pulse" />
          <Flame className="w-14 h-14 text-[#7a3b2e] fill-current streak-flame" />
        </div>
        <div className="font-script text-2xl text-[#7a8b6b] mb-1">Beautiful.</div>
        <h2 className="font-serif-elegant text-4xl md:text-5xl text-[#2f2a24] mb-3">{m.title}</h2>
        {event.recipeTitle && (
          <div className="text-xs uppercase tracking-widest text-[#6f6a5f] mb-4">{event.recipeTitle}</div>
        )}
        <p className="text-[#4a4640] leading-relaxed mb-6">{m.message}</p>
        <button onClick={() => setEvent(null)} className="btn-primary">Keep going</button>
      </div>
    </div>
  );
};

export default StreakCelebration;
export { MILESTONES, getMilestone };
